import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './spreadsheet-chooser.hbs'
import i18n from 'scoreboard/components/i18n'
import FormView from 'scoreboard/components/form-view'
import dropdown from 'scoreboard/components/dropdown'
import ObjectSelector from 'scoreboard/components/object-selector'
import fileInfo from 'scoreboard/components/files/file-info'
import RemoteFilesGardener from './remote-files-gardener'
import Xhr from 'scoreboard/components/xhr'
import googleApi from 'scoreboard/components/google-api'
import SpreadsheetDocumentsGardener from 'scoreboard/imports/spreadsheet-documents-gardener'
import highlight from 'scoreboard/components/highlight'
import center from 'scoreboard/components/min-height-vertical-center-msg.hbs'
import permissions from 'scoreboard/components/permissions'
import * as AdminImportConnections from 'scoreboard/admin/connections/admin-import-connections'
import appDisplay from 'scoreboard/components/app-display'
import RemoteFilesChooser from 'scoreboard/imports/remote-files-chooser'
import EditableButton from 'scoreboard/components/editable-button'
import spreadsheetChooserInstructions from './spreadsheet-chooser-instructions.hbs'
import NewRecordOptions from 'scoreboard/datasets/new-record-options'
import { handleMissingColumnsException } from 'scoreboard/imports/new-column-name-chooser-wizard'
import FileDrop from 'scoreboard/components/files/file-drop'
import Importable from 'scoreboard/imports/model/importable'

const datasetInstructionList = [
	'datasets.dataSource.spreadsheet.instructions1.googleNotEnabled',
	'datasets.dataSource.spreadsheet.instructions2',
	'datasets.dataSource.spreadsheet.instructions3',
	'datasets.dataSource.spreadsheet.instructions4',
	'datasets.dataSource.spreadsheet.instructions5'
]

function getDatasetInstructionsForSpreadsheet() {
	return datasetInstructionList
}

function getDatasetInstructionsForGoogleSheet() {
	return datasetInstructionList.slice(1, 3)
}

function getViInstructionsForSpreadsheet() {
	let viInstructionList = [
		'datasets.dataSource.spreadsheet.instructions1.googleNotEnabled',
		'datasets.dataSource.spreadsheet.instructions4'
	]
	return viInstructionList
}

function getViInstructionsForGoogle() {
	return ['datasets.dataSource.spreadsheet.instructions1.google']
}

function getZapierInstructions() {
	let content =
		`<p class="paragraph">${i18n.get('admin.imports.zapier.instruction1')}</p>
		 <p class="paragraph">${i18n.get('admin.imports.zapier.instruction2')}</p>
		 <p class="paragraph">${i18n.get('admin.imports.zapier.instruction3')}</p>`
	return content
}

/**
 *  adds a bullet point to the instructions for the chooser when no primary key
 *  is set:
 *  "You can't update existing records because no primary key is set on the dataset"
 */
function getDatasetInstructionListNoSetPrimaryKey(instructionList) {
	let datasetInstructionList = _.clone(instructionList)
	datasetInstructionList.push('datasets.dataSource.spreadsheet.instructions6')
	return datasetInstructionList
}

class Chooser {
	/**
	 * @param {SpreadsheetChooser} view
	 * @param {String} id The enum string values from DataSourceType.java
	 * @param {String} label i18n key for the select option
	 * @param {String} icon
	 * @param {String} selector css selector for toggling the chooser's
	 *     visibility
	 */
	constructor(view, id, label, icon, selector) {
		this.view = view
		this.model = view.model
		this.id = id
		this.label = label
		this.icon = icon
		this.selector = selector
	}
	selectOption() {
		return {
			id: this.id,
			label: this.label,
			icon: this.icon
		}
	}
	onSourceChange(id) {
		this.view.$(this.selector).toggleClass('is-not-displayed', id != this.id)
	}
	highlightSelection() {} // abstract, for gardener only
	/**
	 * Makes a tree selection (for the remoteFileGardener)
	 * @param tree
	 * @param node
	 */
	makeTreeSelection(tree, node) {
		if (node) {
			tree.select(node.id, { silent: true, animate: false })
			this.view.formView._updateButtons()
		}
	}
	clearMetadataState(metadata) {
		delete metadata.sheets
	}

	/**
	 * Clears previous fileIds if a different FTP file has been chosen
	 * Used by FtpMultiFileChooser (#34621)
	 * @param metadata
	 * @param previousPath
	 * @param currentPath
	 */
	clearFileIdsIfNeeded(metadata, previousPath, currentPath) {
		if (metadata.source === 'ftp' && previousPath !== currentPath) {
			// the user has selected a different file, so this needs to be
			// cleared for the server to fetch the new file.
			delete metadata.fileIds
			delete metadata.fileId
		}
	}

	/**
	 * #37257 Retrieve the id and label of the currently selected connection
	 * @param selectName the name attribute of the select to retrieve the connection
	 * 					 information from
	 */
	getImportConnection(selectName) {
		const id = this.view.$(`[name="${selectName}"]`).val()
		const label = this.view.$(`[name="${selectName}"]`).text()
		return { id, label }
	}
}

class UploadChooser extends Chooser {
	constructor(view) {
		super(view, 'upload', 'admin.import.spreadsheet.source.upload', 'upload', '.upload-file')
	}
	test() {}
	clearMetadataState(metadata) {
		super.clearMetadataState(metadata)
		delete metadata.uploadFile
	}
	saveState() { /* handled in the file listener */ }
}

class DocumentChooser extends Chooser {
	constructor(view) {
		super(view, 'document', 'admin.import.spreadsheet.source.document', 'docs', '.select-document')
	}
	onSelect() {
		this.view.fileDrop.reset()
		// We need to wait and render the object-selector when `.select-document` is displayed so
		// the trees can adjust their widths properly. Issue #22673
		// We don't need to render this more than once when toggling between source types.
		// The calculations will be set when it's first toggled.
		if (!this.view.objectSelector) {
			this.view.renderObjectSelector()
		}
	}
	test() {
		let selected = _.invoke(this.view.objectSelector, 'selected')
		if (selected) {
			// #32960 do not allow selecting non-spreadsheet documents
			if (_.get(selected, 'type') !== 'file' ||
				_.lowerCase(_.get(selected, 'fileType')) !== 'spreadsheet') {
				return i18n.get('admin.import.errors.selectSpreadsheet',
					highlight(_.get(selected, 'label')))
			}
		} else {
			// prevent submission if no file selected
			return i18n.get('admin.import.errors.selectFile')
		}
	}
	clearMetadataState(metadata) {
		super.clearMetadataState(metadata)
		delete metadata.documentFile
	}
	saveState() {
		const selected = this.view.objectSelector.selected()
		let documentId = _.get(selected, 'id')
		const label = _.get(selected, 'label')
		if (documentId) {
			this.model.get('dataSourceMetadata').documentFile = {
				documentId,
				label
			}
		}
	}
}
class FtpMultiFileChooser extends Chooser {
	constructor(view) {
		super(view, AdminImportConnections.FTP, 'admin.importConnections.ftp.singular',
			AdminImportConnections.FTP, '.ftp-multifile-connection')
	}
	test() {
		const metadata = this.model.get('dataSourceMetadata')
		if (!metadata.ftpFile) {
			return i18n.get('admin.importConnections.ftp.noFileSelected')
		}
		if (!metadata.ftpFile.multipleFiles) {
			return validateFileSelection(metadata.ftpFile.path, true)
		}
	}
	clearMetadataState(metadata) {
		super.clearMetadataState(metadata)
		delete metadata.ftpFile
	}
	saveState(ftpFile) {
		const skipNotModified = this.view.$('.skip-not-modified').is(':checked')
		const dataSourceMetadata = { skipNotModified }
		if (ftpFile) {
			// #34621 clear out previous file ids if the file selection changed
			const metadata = this.model.get('dataSourceMetadata')
			this.clearFileIdsIfNeeded(metadata, _.get(metadata, 'ftpFile.path'),
				ftpFile.path)

			dataSourceMetadata.ftpFile = ftpFile
			// #37257 When editing a datasource, we need this information so we
			// can properly render the connection once it is saved
			dataSourceMetadata.importConnection = this.getImportConnection('ftpConnectionId')
		}

		_.extend(this.model.get('dataSourceMetadata'), dataSourceMetadata)
	}
}

class GoogleFileChooser extends Chooser {
	constructor(view) {
		super(view, 'google', 'admin.import.spreadsheet.source.google',
			'docs-spreadsheet-mono', '.google-connection')
	}
	onSelect() {
		this.view.onChangeGoogleConnectionId()
	}
	test() {
		const remoteGoogleFile = _.result(this.view.remoteFilesGardener, 'tree.selected') || {}
		const {label, remoteId, mimeType, lastModified, size} = remoteGoogleFile
		const fileName = 'application/vnd.google-apps.spreadsheet' === mimeType ? `${label}.xlsx` : label
		const validationMsg = validateFileSelection(fileName)
		if (validationMsg) {
			return validationMsg
		}
		const connectionId = this.view.googleConnectionSelect.val()
		this.view.googleFile = {
			connectionId,
			orbReferenceId: this.view.currentGoogleConnection().orbReferenceId,
			fileId: remoteId,
			fileName: label,
			sizeInBytes: size,
			mimeType,
			lastModified
		}
	}
	highlightSelection() {
		const fileId = this.model.getDataSourceMetadata('googleFile.fileId')
		if (fileId) {
			this.view.remoteFilesGardener._onceTreeLoaded(tree => {
				let node = _.find(tree.nodes, {remoteId:fileId})
				const ancestors = []
				let parentId = node.parentId
				while (parentId) {
					ancestors.push(tree.get(parentId))
					parentId = tree.get(parentId).parentId
				}
				ancestors.reverse().forEach(folder => this.makeTreeSelection(tree, folder))
				this.makeTreeSelection(tree, node)
			})
		}
	}
	clearMetadataState(metadata) {
		super.clearMetadataState(metadata)
		// #30360 only delete the fileId for google files
		if (metadata.source === 'google') {
			delete metadata.fileId
			delete metadata.fileIds
		}
		delete metadata.googleFile
		delete this.view.googleFile
	}

	saveState() {
		const metadata = this.model.get('dataSourceMetadata')
		const googleFile = this.view.googleFile
		const previousGoogleFileId = _.get(metadata, 'googleFile.fileId')
		if (metadata.source === 'google' && googleFile && googleFile.fileId !== previousGoogleFileId) {
			// User just clicked next with a NEW file selection, this has to be
			// cleared for the server to fetch the new file.
			delete metadata.fileId
			delete metadata.fileIds
		}
		if (googleFile) { // If undefined, no new file was selected
			_.extend(metadata, {
				googleFile,
				// #37257 When editing a datasource, we need this information so we
				// can properly render the connection once it is saved
				importConnection: this.getImportConnection('googleConnectionId')
			})
		}
	}
}

class ZapierFileChooser extends Chooser {
	constructor(view) {
		super(view, 'zapier', 'admin.import.spreadsheet.source.zapier',
			'docs-spreadsheet-mono', '.zapier-connection')
	}
	onSelect() {
		this.view.onChangeZapierConnectionId()
	}
	test() {
		const zapData = _.result(this.view.remoteFilesGardener, 'tree.selected') || {}
		const { remoteId, label, zapId } = zapData
		const connectionId = this.view.zapierConnectionSelect.val()
		this.view.zapierFile = {
			zapId,
			connectionId,
			dataStreamId: remoteId,
			dataStreamLabel: label
		}
	}
	highlightSelection() {
		const dataStreamId = this.model.getDataSourceMetadata('zapierFile.dataStreamId')
		if (dataStreamId) {
			this.view.remoteFilesGardener._onceTreeLoaded(tree => {
				let node = _.find(tree.nodes, {remoteId: dataStreamId})
				this.makeTreeSelection(tree, node)
			})
		}
	}
	clearMetadataState(metadata) {
		super.clearMetadataState(metadata)
		// #30360 only delete the fileId for zapier files
		if (metadata.source === 'zapier') {
			delete metadata.fileId
			delete metadata.fileIds
		}
		delete metadata.zapierFile
		delete this.view.zapierFile
	}

	saveState() {
		const metadata = this.model.get('dataSourceMetadata')
		const zapierFile = this.view.zapierFile
		const previousDataStreamId = _.get(metadata, 'zapierFile.dataStreamId')
		if (metadata.source === 'zapier' && zapierFile && zapierFile.dataStreamId !== previousDataStreamId) {
			// User just clicked next with a NEW file selection, this has to be
			// cleared for the server to fetch the new file.
			delete metadata.fileId
			delete metadata.fileIds
		}
		if (zapierFile) { // If undefined, no new file was selected
			_.extend(metadata, {
				zapierFile,
				// #37257 When editing a datasource, we need this information so we
				// can properly render the connection once it is saved
				importConnection: this.getImportConnection('zapierConnectionId')
			})
		}
	}
}


/**
 * Ensures a selected file's extension is valid
 * @param allowZip true if zip files are allowed; false if only spreadsheets are
 */
function validateFileSelection(filename, allowZip = false) {
	let allowedExtensions = ['csv', 'xls', 'xlsx']
	let validExt = !_.isUndefined(filename) &&
		(_.includes(allowedExtensions, fileInfo.extension(filename)) ||
		(allowZip && fileInfo.extension(filename) == 'zip'))
	if (!validExt) {
		return i18n.get('admin.import.errors.selectFile')
	}
}

const SpreadsheetChooser = Backbone.View.extend({

	tagName: 'form',

	className: 'spreadsheet-chooser',

	events: {
		'change .spreadsheet-source': 'onChangeSpreadsheetSource',
		'change [name=googleConnectionId]': 'onChangeGoogleConnectionId',
		'change [name=zapierConnectionId]': 'onChangeZapierConnectionId',
		'change [name=ftpConnectionId]' : 'onChangeFtpConnectionId',
		'click .google-picker': 'onClickGooglePicker',
		'click .import-example-download': 'onImportExampleDownload',
		'click .cancel': 'close',
		'click .add-ftp': 'onAddFtpConnection',
		'click .add-google': 'onAddGoogleConnection',
		'click .add-zapier': 'onAddZapierConnection',
		'click .show-file-selector': 'showFileSelector',
		'click .new-record-options-container li': 'onClickNewRecordOption'
	},

	/**
	 * Form for choosing a spreadsheet to upload, or from the documents section,
	 * etc. Usually part of a wizard. Used when creating datasets, value imports
	 * and scorecard item imports (and possibly other places as well)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Import|Dataset} options.model
	 * @param {DataSourceMetadata.kt} options.model.dataSourceMetadata
	 *
	 * @param {Array} options.connections array of connections the user either
	 * has access to or can view because they are used by one of the imports the
	 * user has access to
	 *
	 * @param {String} [options.title] optional title for the dialog
	 * @param {Boolean} [options.example] whether to display 'Download Example' button
	 * @param {String} [options.downloadUrl] url for downloading an example file
	 * @param {String} [options.instructions] additional instructions to display
	 * @param {Boolean} [options.useDatasetInstructions] displays more detailed
	 * 					instructions for selecting files for datasets
	 * @param {Boolean} [options.showReplaceAllRecordOptions=false] whether to
	 * show the box options for "Add & Update Records" vs "Replace All Records"
	 * @param {Boolean} [options.dataSourceTypeChosen] if the user has already
	 * selected the dataSource type (ex via a wizard that is using
	 * data-source-type-chooser.js)
	 * @param {Object} [options.labels] label information for building title
	 * @param {Boolean} [options.useViInstructions] display instructions that
	 * are appropriate for value imports
	 */
	initialize: function (options) {
		this.options = options || {}
		this.connectionById = _.keyBy(this.options.connections, 'id')
		// #53861 if the user is not an owner of any connections,
		// this.options.connections will be empty
		// If there is a current connection, add that to this.connectionsById
		// for retrieving the connection later
		this.currentConnection = this.options.currentConnection
		let currentConnectionId = this.currentConnection?.id
		if (currentConnectionId && _.isNil(this.connectionById[currentConnectionId])) {
			this.connectionById[currentConnectionId] = this.currentConnection
		}
		this.replaceAllRecords = this.model.get('replaceAllRecords')
		// check to see if the data source type has already been selected
		// using the new flow for choosing import data source
		let dataSourceTypeChosen = _.get(this.options, 'dataSourceTypeChosen')
		// if populated, we will want to hide the dropdown of spreadsheet types
		this.hideSpreadsheetOptions = !_.isNil(dataSourceTypeChosen)

		this.initializeFilesAndFileDrop()
		this.initializeFormView()
		this.initializeChoosers()

		Backbone.on('add:import-connection', connection => {
			_.remove(this.options.connections, it => it.id == connection.id)
			this.options.connections.unshift(connection)
			this.connectionById = _.keyBy(this.options.connections, 'id')
			this.populateConnectionSelects()
			if (connection.type === AdminImportConnections.GOOGLE_SHEETS) {
				this.onChangeGoogleConnectionId()
			} else if (connection.type === AdminImportConnections.ZAPIER) {
				this.onChangeZapierConnectionId()
			}
		})
	},

	initializeFormView: function() {
		let MyFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyFormView({
			el: this.el,
			guard: false,
			test: this.test.bind(this),
			submit: this.submit.bind(this),
			allowCleanSubmission: () => {
				// #35710 No file is created when selecting an FTP path, so we
				// need to check that separately of the fileId
				const ftpFile = this.model.getDataSourceMetadata('ftpFile')
				return !this.model.isNew() || this.model.getDataSourceMetadata('fileId') ||
					(ftpFile && ftpFile.path)
			}
		})
	},

	initializeFilesAndFileDrop: function() {
		const namePath = 'uploadFile.name'
		const modifiedPath = 'uploadFile.lastModified'
		let params = ''
		if (!_.isNil(this.options.isShowingPreviewNext)) {
			params = $.param({
				preview: this.options.isShowingPreviewNext
			})
		}

		this.fileDrop = new FileDrop({
			animated: true,
			fileUploaderOptions: _.extend({}, {
				url: `${this.model.getUploadSpreadsheetUrl()}?${params}`,
				// if we're uploading a new spreadsheet to an existing import
				dataFn: this.onFileUploaderData.bind(this)
			}, this._defaultFileUploaderOptions(namePath, modifiedPath))
		})

		this.setupFileDropListeners()
	},

	/**
	 * Returns the payload to send to the server when a file upload is selected
	 * by the user.  The file information will be added to whatever is returned
	 * here.
	 */
	onFileUploaderData() {
		const data = {}
		if (!this.model.isNew() && this.model.get('type') === 'dataset') {
			data.datasetId = this.model.id
		}
		// if we're uploading a new spreadsheet to an existing import
		if (!this.options.wizard) {
			data.importId = this.model.id
		}
		return data
	},

	_defaultFileUploaderOptions: function(namePath, modifiedPath) {
		const file = this._readFileFromMetadata(namePath, modifiedPath)
		return {
			files: _.isUndefined(file) ? [] : [file],
			alreadyUploaded: this.model.getDataSourceMetadata('fileId'),
			accept: [this.options.accept || 'spreadsheetWithoutNumbers']
		}
	},

	_readFileFromMetadata: function (namePath, modifiedPath) {
		const name = this.model.getDataSourceMetadata(namePath)
		const lastModified = this.model.getDataSourceMetadata(modifiedPath)
		return _.isNil(name) ? undefined : {name, lastModified}
	},

	setupFileDropListeners(fileUploader) {
		this.fileDrop.on('done', dataSourceMetaData => {
			// #36957 only run this if the file uploaded successfully
			// and we got a response from the server
			if (dataSourceMetaData) {
				_.extend(this.model.get('dataSourceMetadata'), dataSourceMetaData)
				_.invoke(this.options.wizard, 'setNewColumnChooserIndex', null)
				if (this.model.isNew()) {
					// #35718 If we are creating a new dataset, go ahead and continue;
					// otherwise stay on this screen so the user can decide whether
					// to update or replace current records
					this.submit()
				} else {
					this.formView.primaryButton.done()
					this.formView.change()
				}
			}
			})
			.on('error', xhr => this.uploadError(xhr, fileUploader))
			.on('start', () => this.uploadStart())
			.on('reset', () => this.uploadReset())
	},

	initializeChoosers() {
		this.uploadChooser = new UploadChooser(this)
		this.documentFileChooser = new DocumentChooser(this)
		this.ftpMultiFileChooser = new FtpMultiFileChooser(this)
		this.googleChooser = new GoogleFileChooser(this)
		this.zapierChooser = new ZapierFileChooser(this)

		this.allChoosers = [this.uploadChooser, this.documentFileChooser,
			this.ftpMultiFileChooser, this.googleChooser, this.zapierChooser]
		this.fileChooserById = _.keyBy(this.allChoosers, 'id')

		this.enabledChoosers = [this.uploadChooser, this.documentFileChooser]
		// with #48217, we are going enable the choosers even if the user does
		// not have modifyImportConnections permission or if user does not have
		// any available connections of that type (this reverts logic added for #33113)
		// Instead we will be modifying the display based on the permissions and
		// available connections for the user
		this.enabledChoosers.push(this.ftpMultiFileChooser)
		// #33179 do not display Google sheets as an option if it is not enabled
		if (googleApi.isAvailable()) {
			this.enabledChoosers.push(this.googleChooser)
		}
		this.enabledChoosers.push(this.zapierChooser)
	},

	getChooserId() {
		const chooserId = this.$('.spreadsheet-source').val()
		if (!_.isNil(chooserId)) return chooserId

		return _.get(this.allChoosers[0], 'id')
	},

	/**
	 * The chooser instance the user is currently selecting a file from
	 */
	activeChooser() {
		return this.fileChooserById[this.getChooserId()]
	},

	render: function () {
		this.$el.empty().append(template(this.context()))

		this.ftpConnectionSelect = this.$('[name=ftpConnectionId]')
		this.googleConnectionSelect = this.$('[name=googleConnectionId]')
		this.zapierConnectionSelect = this.$('[name=zapierConnectionId]')

		this.options.wizard && this.options.wizard.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})

		// #35111 this view was still hanging around the wizard completed if
		// "rename columns" dialog was opened
		this.options.wizard && this.options.wizard.on('done', () => this.close())

		this.renderSpreadsheetSource()
		this.populateConnectionSelects()

		this.$('.upload-file').append(this.fileDrop.el)
		this.fileDrop.render()

		this.showFileSelectorButton = new EditableButton({
			el: this.$('.show-file-selector'),
			message: 'admin.importConnections.ftp.chooseFiles',
			label: this.model.getDataSourceMetadata('ftpFile.path')
		})

		this.showFileSelectorButton.render()
		this.newRecordOptions = new NewRecordOptions({
			replaceAllRecords: this.model.get('replaceAllRecords'),
			noConfirmation: true,
			hasPrimaryKeyField: this.model?.hasPrimaryKeyField?.() // "add" vs. "add & update"
		})
		this.$('.new-record-options-container').html(this.newRecordOptions.el)
		this.newRecordOptions.render()

		// #37039 set the initial state for the "skip not modified" checkbox
		this.$('.skip-not-modified').prop('checked',
			this.model.get('dataSourceMetadata').skipNotModified)

		// show the correct controls on the form based on the spreadsheet
		// source
		this.onChangeSpreadsheetSource()
		this.renderBanner()

		this.formView.start()
		return this
	},

	renderSpreadsheetSource: function() {
		// source inputs
		const options = _.map(this.enabledChoosers, c => c.selectOption())
		const metadata = this.model.getDataSourceMetadata()
		const chooserId = metadata.source != null ? metadata.source : this.getChooserId()
		dropdown.idLabels(this.$('select.spreadsheet-source').empty(), options,
			this.fileChooserById[chooserId].id)
		if (this.hideSpreadsheetOptions) {
			this.$('.spreadsheet-source-label').addClass('is-not-displayed')
		}
	},

	renderBanner: function() {
		let hasNoInstructions = this.options.dataSourceTypeObj?.type === 'zapier'
		this.$('.banner:not(.banner-error)').empty().append(spreadsheetChooserInstructions({
			banner: this.bannerInstructions(),
			listInstructions: hasNoInstructions ? false :
				(this.options.useDatasetInstructions || this.options.useViInstructions),
			example: hasNoInstructions ? false : this.options.example
		}))
		// #48217 if there are no available connections and the user cannot add
		// add a new connection, display a message in the banner
		if (this.noAvailableConnections() && !this.hasModifyImportConnectionPermission()) {
			let message = i18n.get('admin.import.connections.none.withArg', this.getSourceLabel())
			this.$('.banner:not(.banner-error)').empty().append(message)
		}
	},

	getSourceLabel: function() {
		let sourceLabel = i18n.get('charts.chart.selected')
		let source = this.getChooserId()
		if (source === this.ftpMultiFileChooser.id) {
			sourceLabel = i18n.get('admin.importConnections.acronym.sftp')
		} else if (source === this.googleChooser.id) {
			sourceLabel = i18n.get('admin.import.spreadsheet.source.google')
		} else if (source === this.zapierChooser.id) {
			sourceLabel = i18n.get('admin.import.spreadsheet.source.zapier')
		}
		return sourceLabel
	},

	bannerInstructions: function() {
		let banner
		if (this.options.dataSourceTypeObj?.type === 'zapier') {
			banner = getZapierInstructions()
		} else if (this.options.useDatasetInstructions) {
			banner = this.activeChooser() == this.googleChooser ?
				getDatasetInstructionsForGoogleSheet()
				: getDatasetInstructionsForSpreadsheet()
			// #42726 add extra bullet point if no primary key is set on dataset
			// (when editing existing dataset)
			if (!this.model.isNew() && !this.model?.hasPrimaryKeyField()) {
				banner = getDatasetInstructionListNoSetPrimaryKey(banner)
			}
		} else if (this.options.useViInstructions) {
			// #50884 use different language for google and non-google spreadsheet sources
			banner = this.activeChooser() == this.googleChooser ?
				getViInstructionsForGoogle() : getViInstructionsForSpreadsheet()
		} else {
			// #50884 use different language based for google and non-google spreadsheet sources
			// using the same message key as vi imports for the first line
			let defaultMessageKey = this.activeChooser() == this.googleChooser ?
				'datasets.dataSource.spreadsheet.instructions1.google' :
				'datasets.dataSource.spreadsheet.instructions1.googleNotEnabled'
			banner = i18n.get(defaultMessageKey)
			// if additional instructions were provided, append those following the default message
			// (ex. scorecard item and initiative item imports)
			if (this.options.instructions) {
				banner = banner + this.options.instructions
			}
		}
		return banner
	},

	/**
	 * Returns the available import connections for the given connection type
	 * that are online.
	 * If no connections are available due to permissions, but the user is able to
	 * edit the importable, the current import connection is included
	 */
	getAvailableConnections: function(connectionType) {
			let connections = _.cloneDeep(this.options.connections)
			// filter by connection type and online status
			connections = _.filter(connections, connection =>
				connection.type === connectionType &&
				connection.status === 'online')
			// if the user isn't an owner of the existing connection, it won't be
			// passed into this.options.connections. That's fine, they have access
			// to the importable, so they can also see the connection so go ahead and add it
			// after filtering
			const {id, label, type} = this.currentConnection || {}
			if (id && type == connectionType && !_.find(connections, c => c.id == id)) {
				connections.push({
					id,
					label,
					type
				})
			}
			// then sort by label
			return _.sortBy(connections, c => c.label.toLocaleLowerCase())
	},

	/*
	 * Returns the online FTP connections
	 */
	getOnlineFtpConnections: function() {
		return this.getAvailableConnections(AdminImportConnections.FTP)
	},

	getGoogleConnections: function() {
		return this.getAvailableConnections(AdminImportConnections.GOOGLE_SHEETS)
	},

	getZapierConnections: function() {
		return this.getAvailableConnections(AdminImportConnections.ZAPIER)
	},


	populateConnectionSelects: function() {
		this._renderFtpConnectionOptions()
		this._renderGoogleConnectionOptions()
		this._renderZapierConnectionOptions()
	},

	_renderFtpConnectionOptions: function () {
		let ftpConnections = this.getOnlineFtpConnections()
		let noConnections = _.isEmpty(ftpConnections)
		// 'choose files' button & 'skip files' toggle shouldn't be visible if
		// no connections
		this.$('.show-file-selector, .show-file-selector + label')
			.toggleClass('is-not-displayed', noConnections)

		this._renderConnectionOptions(this.ftpConnectionSelect,
			ftpConnections, 'ftpFile.connectionId')
	},

	_renderGoogleConnectionOptions: function () {
		if (googleApi.isAvailable) {
			this._renderConnectionOptions(this.googleConnectionSelect,
				this.getGoogleConnections(), 'googleFile.connectionId')
		}
	},

	_renderZapierConnectionOptions: function () {
		this._renderConnectionOptions(this.zapierConnectionSelect,
			this.getZapierConnections(), 'zapierFile.connectionId')
	},

	_renderConnectionOptions: function(selectEl, connections, connectionIdPath) {
		// #56288 if the selectEl is undefined, don't try to render the options
		// (to prevent errors when running client tests that trigger this method)
		if (_.isNil(selectEl)) return
		selectEl.attr('disabled', _.isEmpty(connections))
		dropdown.idLabels(selectEl.empty(), connections,
			this.model.getDataSourceMetadata(connectionIdPath))
	},

	renderObjectSelector: function() {
		this.objectSelector = new ObjectSelector({
			section: 'files',
			orgId: this.model.getDataSourceMetadata('documentFile.organizationId'),
			id: this.model.getDataSourceMetadata('documentFile.documentId'),
			showProjectSelector: true,
			objectGardener: SpreadsheetDocumentsGardener
		})
		this.$('.select-document .object-selector').replaceWith(this.objectSelector.el)
		this.objectSelector.render()

		let formChange = this.formView.change.bind(this.formView)
		this.objectSelector.on('select selectOrg', formChange)
		this.objectSelector.objectGardener._onceTreeRendered(formChange)
	},

	renderRemoteFileGardener: function(connectionId) {
		this.$('.remote-files-connection, .remote-files-connection .panel')
			.removeClass('is-not-displayed')
		this.clearEmptyGardenerMessage()
		if (connectionId) {
			if (!this.remoteFilesGardener) {
				this.remoteFilesGardener = new RemoteFilesGardener({connectionId})
				this.$('.gardener-container').append(this.remoteFilesGardener.el)
				this.remoteFilesGardener.render()
				this.remoteFilesGardener.on('select', () => this.formView.change())
				this.activeChooser().highlightSelection()
			} else {
				// #31823 display loading dots when changing between connections
				this.remoteFilesGardener.insertLoadingDots()
				this.remoteFilesGardener.show({connectionId})
			}
			this.$('.finder').addClass('finder-light')
			this.$('.tree-container').addClass('tree-container-light')
		} else {
			this.showEmptyFileGardener()
			// #48217 don't display the "Select Files" panel if a connection
			// is not selected
			this.$('.remote-files-connection').addClass('is-not-displayed')
		}
	},

	showEmptyFileGardener: function() {
		let noConnectionsMsg = i18n.get(
			'admin.import.metricValue.noConnectionsForType',
			this.getSourceLabel())
		let noConnectionsDisplay = center(noConnectionsMsg)
		// #33151 hide any existing gardener (instead of removing)
		this.$('.gardener').addClass('is-not-displayed')
		this.$('.gardener-container').append(noConnectionsDisplay)
	},

	clearEmptyGardenerMessage() {
		// #33151 remove any existing "no connections" message
		this.$('.min-height-vertical-center').remove()
		this.$('.gardener').removeClass('is-not-displayed')
	},

	onChangeSpreadsheetSource: function() {
		this.formView.change()
		const dataSourceType = this.$('.spreadsheet-source').val()
		this.model.setSpreadsheetSourceSelection(dataSourceType)
		_.each(this.allChoosers, c => c.onSourceChange(dataSourceType))
		_.invoke(this.fileChooserById[dataSourceType], 'onSelect')

		this.$('.add-connection').addClass('is-not-displayed')
		const connectionType = this.$('.spreadsheet-source').val()
		// #48217 only display "new connection" link if user has permission
		if (this.hasModifyImportConnectionPermission()) {
			this.$(`.add-${connectionType}`).removeClass('is-not-displayed')
		}
		let isZapier = connectionType === 'zapier'
		this.$('.default-header').toggleClass('is-not-displayed', isZapier)
		this.$('.zapier-header').toggleClass('is-not-displayed', !isZapier)
		this.renderBanner()
	},

	noAvailableConnections() {
		let chooser = this.activeChooser()
		if (chooser == this.googleChooser) {
			return _.isEmpty(this.getGoogleConnections())
		} else if (chooser == this.zapierChooser) {
			return _.isEmpty(this.getZapierConnections())
		} else if (chooser == this.ftpMultiFileChooser) {
			return _.isEmpty(this.getOnlineFtpConnections())
		}
		return false // doesn't apply to other choosers
	},

	hasModifyImportConnectionPermission: function() {
		return permissions.modifyImportConnections()
	},

	/**
	 * user just changed the google connection
	 */
	onChangeGoogleConnectionId: function() {
		const connectionId = this.googleConnectionSelect.val()
		this.renderRemoteFileGardener(connectionId)
		delete this.googleFile
	},

	/**
	 * user just changed the zapier connection
	 */
	onChangeZapierConnectionId: function() {
		const connectionId = this.zapierConnectionSelect.val()
		this.renderRemoteFileGardener(connectionId)
		delete this.zapierFile
	},

	/**
	 * user just changed the ftp connection
	 */
	onChangeFtpConnectionId: function() {
		// #54049 when changing ftp connections, clear out any existing metadata
		const metadata = this.model.get('dataSourceMetadata')
		delete metadata.ftpFile
		delete metadata.fileIds
		delete metadata.fileId
		this.model.set('dataSourceMetadata', metadata)
		// clear the label in the file selector button
		this.showFileSelectorButton.renderLabel()
	},

	/**
	 * Downloads an example import file
	 */
	onImportExampleDownload() {
		new Xhr().download({url: this.options.downloadUrl})
	},

	context: function () {
		let title = this.options.title || i18n.get('admin.import.spreadsheet.title')

		// #44888 title for importables using new datasource selection flow
		if (this.model instanceof Importable) {
			let titleKey, typeKey
			if (this.model.get('type') === 'dataset') {
				typeKey = 'datasets.dataset'
				const dataSourceTypeKey = this.model.get('source') === 'zapier' ?
					'admin.import.datasource.from.zapier.create' :
					'admin.import.datasource.from.spreadsheet.create'
				titleKey = this.model.isNew() ? dataSourceTypeKey :
					'admin.import.datasource.type.edit'
			} else {
				// value imports
				if (this.model.isNew()) {
					if (this.options.dataSourceTypeObj?.type === 'zapier') {
						titleKey = 'admin.import.datasource.from.zapier'
					} else {
						titleKey = 'admin.import.datasource.from.spreadsheet'
					}
					typeKey = _.get(this.options, 'labels.plural', 'admin.import')
				} else {
					titleKey = 'admin.import.datasource.type.edit'
					typeKey = _.get(this.options, 'labels.singular', 'admin.import')
				}
			}
			title = i18n.get(titleKey, typeKey)
		}

		return {
			title,
			showReplaceAllRecordOptions: this.options.showReplaceAllRecordOptions
		}
	},

	currentGoogleConnection: function() {
		const connectionId = this.googleConnectionSelect.val()
		return this.connectionById[connectionId]
	},

	uploadReset: function () {
		this.formView.change()

		// Clear state from metadata so file is not reconstructed on back/forward nav
		const metadata = this.model.getDataSourceMetadata()
		this.activeChooser().clearMetadataState(metadata)

		// delete file and don't allow navigating forward in wizard
		if (this.options.wizard) {
			if (this.model.isNew()) this.model.deleteFile()
			// don't allow navigating to the next step
			this.options.wizard.lastNavigable = this.options.wizard.step
			// stay at current step, no transition when rendering wizard
			this.options.wizard.previousStep = this.options.wizard.step
			this.options.wizard && this.options.wizard.render({
				formView: this.formView
			})
		}
	},

	uploadError: function(xhr) {
		// #36957 This prevents the new column name chooser from trying to
		// submit the changes to the server
		_.invoke(this.options.wizard, 'setSubmitHandler', columnMap => {
			this.model.set('newColumnNames', columnMap)
			this._doSubmit()
		})
		this.newColumnChooserOptions = _.extend({}, this.options, {
			replaceAllRecords: this.replaceAllRecords
		})
		// #33702 if the uploaded spreadsheet is missing columns, we need to
		// let the user map them to available columns on the spreadsheet
		if (handleMissingColumnsException(xhr, this.newColumnChooserOptions, false)) {
			const fileId = xhr.responseJSON && xhr.responseJSON.fileId
			const fileName = this.$('.upload-file input').val()
			// #37206 because the server threw an error, we need to manually
			// apply the data source changes
			_.extend(this.model.get('dataSourceMetadata'), {
				fileId: fileId,
				fileIds: [ fileId ],
				label: fileName,
				uploadFile: {
					name: fileName,
					lastModified: new Date()
				}
			})
			// if this isn't the last step of the wizard, we don't want to apply the
			// replace behavior yet
			if (this.options.wizard.isLastStep()) {
				this.model.set('replaceAllRecords', this.replaceAllRecords)
			}
			this.options.wizard.setNewColumnChooserIndexLast()
			this.fileDrop.fileUploader.uploadFinished()
		} else {
			if (xhr.statusText !== 'abort') {
				this.formView.error(xhr)
			}
			this.fileDrop.reset()
		}
	},

	uploadStart: function() {
		// #33039 prevent clicking on the Next button while
		// the file is still being uploaded
		_.result(this.formView, 'primaryButton.loading')
		_.result(this.formView, 'clearErrors')
	},

	test: function () {
		var msg = FormView.prototype.test.apply(this, arguments)
		if (msg) {
			return msg
		}
		return this.activeChooser().test()
	},

	saveState: function() {
		this.activeChooser().saveState()
	},

	close: function() {
		_.invoke(this.xhr, 'abort')
		this.trigger('close')
	},

	onAddFtpConnection: function() {
		AdminImportConnections.addFtp()
	},

	onAddGoogleConnection: function() {
		this.authBroadcastChannel = AdminImportConnections.addGoogleSheets()
	},

	onAddZapierConnection: function() {
		AdminImportConnections.addZapier()
	},

	showFileSelector: function(event) {
		// by default, any button on the dialog advances the wizard
		event.preventDefault()

		this.formView.clearErrors()
		const connectionId = this.ftpConnectionSelect.val()
		const fileChooser = new RemoteFilesChooser({ connectionId, model: this.model })
		fileChooser.on('done', e => {
			if (e) {
				// if we picked a new file, clear out the fileIds in the existing metadata
				const metadata = this.model.get('dataSourceMetadata')
				delete metadata.fileIds
				delete metadata.fileId
				this.model.set('dataSourceMetadata', metadata)

				this.showFileSelectorButton.renderLabel(e.path)
				this.activeChooser().saveState(e)
			}
			// #34539 Next button should be re-enabled after selecting file
			this.formView.change()
		})
		appDisplay.dialog(fileChooser)
	},

	onClickNewRecordOption: function(e) {
		this.newRecordOptions.handleOptionSelected(e, replaceAllRecords =>
			// don't save replaceAllRecords to this.model until the user finishes the wizard
			this.replaceAllRecords = replaceAllRecords
		)
	},

	_doSubmit: function() {
		// Only clear the metadata state for other choosers right before submit,
		// this lets the client maintain the state of each chooser for the user
		const dataSourceType = this.$('.spreadsheet-source').val()
		this.model.getDataSourceMetadata().source = dataSourceType
		let otherChoosers = _.filter(this.allChoosers, c => c.id !== dataSourceType)

		_.each(otherChoosers,
			chooser => chooser.clearMetadataState(this.model.getDataSourceMetadata()))

		// #53468 remove header row index to reset validation
		if (this.model.getDataSourceMetadata().header) {
			delete this.model.attributes.dataSourceMetadata.headerRow
		}

		this.saveState()

		const url = _.result(this.model, 'getSourceDataPreviewUrl') +
			'?validateColumnNames=false'
		this.xhr = $.ajax({
			method: 'post',
			data: JSON.stringify(this.model),
			url,
			success: response => {
				this.model.set(response)
				if (this.options.wizard.isLastStep()) {
					this.options.wizard.finish(this.model)
					return
				}
				this.options.wizard.next(_.extend({}, this.options, {
					replaceAllRecords: this.replaceAllRecords
				}))
			},
			error: (xhr) => {
				_.invoke(this.options.wizard, 'setNewColumnChooserIndexLast')
				_.invoke(this.options.wizard, 'setSubmitHandler', columnMap => {
					this.model.set('newColumnNames', columnMap)
					this._doSubmit()
				})
				if (!handleMissingColumnsException(xhr, this.options)) {
					this.formView.error(xhr)
				}
			}
		})
		return this.xhr
	},

	/**
	 * If there was a MissingColumnsException, show the NewColumnChooser dialog and returns true, else
	 * returns false
	 */
	_showNewColumnOptionsIfNeeded: function() {
		// if there was a MissingColumnsException when uploading the spreadsheet, and the user needs to
		// select new columns then do that before getting the preview
		if (!_.isNil(this.options.wizard.newColumnChooserIndex)) {
			// wizard.step is 1-indexed, so if the next step is the new column chooser, go to it
			if (this.options.wizard.step === this.options.wizard.newColumnChooserIndex) {
				this.options.wizard.next(this.newColumnChooserOptions)
				return true
			}

			// if the next step is not the new column chooser, save this.newColumnChooserOptions for later
			this.options = _.extend({}, this.options, this.newColumnChooserOptions)
		}

		return false
	},

	_submitHelper: function() {
		if (this._showNewColumnOptionsIfNeeded()) {
			return
		}

		return this._doSubmit()
	},

	submit: function () {
		if (this.model.isNew()) {
			return this._submitHelper()
		} else {
			// #35718 Prompt user to confirm that they want to overwrite records
			// before moving to the next screen
			return this.newRecordOptions.confirmReplaceIfNeeded((replaceAllRecords, cancelled) => {
				if (cancelled) {
					this.formView.start()
					this.formView.change()
				} else {
					if (this.options.wizard.isLastStep()) {
						this.model.set('replaceAllRecords', replaceAllRecords)
					}
					return this._submitHelper()
				}
			})

		}
	},


	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.fileDrop,
			this.objectSelector, this.remoteFilesGardener,
			this.showFileSelectorButton, this.newRecordOptions], 'remove')
		_.invoke(this.authBroadcastChannel, 'close')
	}

})

export default SpreadsheetChooser

SpreadsheetChooser.__filename__ = 'imports/spreadsheet-chooser.js'
